import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  User,
  Mail,
  Phone,
  MapPin,
  GraduationCap,
  Briefcase,
  Code,
  Award,
  Heart,
  FileText,
  Database,
} from "lucide-react";
import type { Tables } from "@/integrations/supabase/types";
import {
  formatCandidateStatus,
  formatBranchType,
  formatQualificationType,
  getEvaluationRoundLabel,
} from "@/utils/formatters";
import { getCandidateStatusColor } from "@/utils/statusColors";

type Candidate = Tables<"candidates"> & {
  colleges?: { name: string } | null;
  job_positions?: { role_name: string } | null;
  evaluation_rounds?: { round_name: string; score: number | null }[];
};

interface CandidateDetailDialogProps {
  candidate: Candidate | null;
  open: boolean;
  onClose: () => void;
  onUpdate: () => void;
}

// Helper function to render JSON array data as readable text
const renderArrayData = (data: any, title: string) => {
  if (!data || !Array.isArray(data) || data.length === 0) {
    return (
      <div className="text-sm text-gray-500 italic">
        No {title.toLowerCase()} provided
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {data.map((item, index) => (
        <div key={index} className="p-3 bg-gray-50 rounded-lg border">
          <p className="text-sm text-gray-700">{item}</p>
        </div>
      ))}
    </div>
  );
};

// Helper function to render parsed resume data
const renderParsedResumeData = (data: any) => {
  if (!data) {
    return (
      <div className="text-sm text-gray-500 italic">
        No parsed resume data available
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Basic Information
          </h4>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Name:</span>{" "}
              {data.full_name || "N/A"}
            </p>
            <p>
              <span className="font-medium">Email:</span> {data.email || "N/A"}
            </p>
            <p>
              <span className="font-medium">Phone:</span> {data.phone || "N/A"}
            </p>
            <p>
              <span className="font-medium">CGPA:</span> {data.cgpa || "N/A"}
            </p>
          </div>
        </div>
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Education
          </h4>
          <div className="space-y-2 text-sm">
            <p>
              <span className="font-medium">Qualification:</span>{" "}
              {data.qualification || "N/A"}
            </p>
            <p>
              <span className="font-medium">Branch:</span>{" "}
              {data.branch || "N/A"}
            </p>
            <p>
              <span className="font-medium">College:</span>{" "}
              {data.college_university || "N/A"}
            </p>
          </div>
        </div>
      </div>

      {data.summary && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">Summary</h4>
          <p className="text-sm text-gray-600">{data.summary}</p>
        </div>
      )}

      {data.key_skills && data.key_skills.length > 0 && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Key Skills
          </h4>
          <div className="flex flex-wrap gap-2">
            {data.key_skills.map((skill: string, index: number) => (
              <Badge key={index} variant="secondary">
                {skill}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {data.technologies && data.technologies.length > 0 && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Technologies
          </h4>
          <div className="flex flex-wrap gap-2">
            {data.technologies.map((tech: string, index: number) => (
              <Badge key={index} variant="outline">
                {tech}
              </Badge>
            ))}
          </div>
        </div>
      )}

      {data.projects_details && data.projects_details.length > 0 && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">Projects</h4>
          {renderArrayData(data.projects_details, "Projects")}
        </div>
      )}

      {data.internships_experience &&
        data.internships_experience.length > 0 && (
          <div>
            <h4 className="font-semibold text-sm text-gray-700 mb-2">
              Experience
            </h4>
            {renderArrayData(data.internships_experience, "Experience")}
          </div>
        )}

      {data.certifications_awards && data.certifications_awards.length > 0 && (
        <div>
          <h4 className="font-semibold text-sm text-gray-700 mb-2">
            Certifications & Awards
          </h4>
          {renderArrayData(
            data.certifications_awards,
            "Certifications & Awards"
          )}
        </div>
      )}
    </div>
  );
};

export function CandidateDetailDialog({
  candidate,
  open,
  onClose,
  onUpdate,
}: CandidateDetailDialogProps) {
  if (!candidate) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader className="text-center">
          <DialogTitle className="flex flex-col items-center gap-2">
            <div className="flex items-center gap-2">
              <User className="h-5 w-5" />
              <span className="font-semibold text-lg">
                {candidate.full_name}
              </span>
            </div>
            <Badge className={getCandidateStatusColor(candidate.status)}>
              {formatCandidateStatus(candidate.status)}
            </Badge>
          </DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="details" className="w-full">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="details">Candidate Details</TabsTrigger>
            <TabsTrigger value="parsed-data">Parsed Resume Data</TabsTrigger>
            <TabsTrigger value="raw-resume">Raw Resume</TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-6">
            {/* Basic Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="h-4 w-4" />
                  Basic Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-center gap-2">
                    <Mail className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">{candidate.email}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Phone className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">
                      {candidate.phone || "Not provided"}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <MapPin className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">
                      {candidate.permanent_city || "Not provided"}
                    </span>
                  </div>
                  {candidate.current_city && (
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-gray-500" />
                      <span className="text-sm">{candidate.current_city}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4 text-gray-500" />
                    <span className="text-sm">
                      {candidate.job_positions?.role_name ||
                        candidate.position_applied ||
                        "Not specified"}
                    </span>
                  </div>
                </div>
                {candidate.permanent_address && (
                  <div>
                    <p className="text-sm font-medium text-gray-700">
                      Address:
                    </p>
                    <p className="text-sm text-gray-600">
                      {candidate.permanent_address}
                    </p>
                  </div>
                )}
                {candidate.current_address && (
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-700">Current Address:</p>
                    <p className="text-sm text-gray-600">{candidate.current_address}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Educational Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <GraduationCap className="h-4 w-4" />
                  Education
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <p className="font-medium text-gray-700">College:</p>
                    <p className="text-gray-600">
                      {candidate.college_university ||
                        candidate.colleges?.name ||
                        "Not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Qualification:</p>
                    <p className="text-gray-600">
                      {formatQualificationType(candidate.qualification)}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">Branch:</p>
                    <p className="text-gray-600">
                      {formatBranchType(candidate.branch)}
                    </p>
                  </div>
                  <div>
                    <p className="font-medium text-gray-700">CGPA:</p>
                    <p className="text-gray-600">
                      {candidate.cgpa || "Not provided"}
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scores */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Scores
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 text-sm">
                  <div>
                    <span className="font-medium">Final Score:</span>{" "}
                    {candidate.final_score != null
                      ? `${candidate.final_score}%`
                      : "N/A"}
                  </div>
                  {candidate.evaluation_rounds &&
                    candidate.evaluation_rounds.length > 0 && (
                      <div className="space-y-1">
                        {candidate.evaluation_rounds.map((er, idx) => (
                          <div key={idx}>
                            {getEvaluationRoundLabel(er.round_name as any)}:{" "}
                            {er.score != null ? `${er.score}%` : "N/A"}
                          </div>
                        ))}
                      </div>
                    )}
                </div>
              </CardContent>
            </Card>

            {/* Skills and Technologies */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Code className="h-4 w-4" />
                  Skills & Technologies
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-medium text-gray-700 mb-2">Key Skills:</p>
                  {candidate.key_skills &&
                  Array.isArray(candidate.key_skills) &&
                  candidate.key_skills.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {candidate.key_skills.map((skill, index) => (
                        <Badge key={index} variant="secondary">
                          {skill}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      No skills listed
                    </p>
                  )}
                </div>
                <div>
                  <p className="font-medium text-gray-700 mb-2">
                    Technologies:
                  </p>
                  {candidate.technologies &&
                  Array.isArray(candidate.technologies) &&
                  candidate.technologies.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                      {candidate.technologies.map((tech, index) => (
                        <Badge key={index} variant="outline">
                          {tech}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">
                      No technologies listed
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Projects */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Projects
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderArrayData(candidate.projects_details, "Projects")}
              </CardContent>
            </Card>

            {/* Project Links */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Project Links
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderArrayData(candidate.project_links, "Project Links")}
              </CardContent>
            </Card>

            {/* Experience */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Briefcase className="h-4 w-4" />
                  Experience
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderArrayData(
                  candidate.internships_experience,
                  "Experience"
                )}
              </CardContent>
            </Card>

            {/* Certifications & Awards */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Certifications & Awards
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderArrayData(
                  candidate.certifications_awards,
                  "Certifications & Awards"
                )}
              </CardContent>
            </Card>

            {/* Event Participations */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Award className="h-4 w-4" />
                  Event Participations
                </CardTitle>
              </CardHeader>
              <CardContent>
                {renderArrayData(
                  candidate.event_participations,
                  "Event Participations"
                )}
              </CardContent>
            </Card>

            {/* Hobbies & Interests */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Heart className="h-4 w-4" />
                  Hobbies & Interests
                </CardTitle>
              </CardHeader>
              <CardContent>
                {candidate.hobbies_interests &&
                Array.isArray(candidate.hobbies_interests) &&
                candidate.hobbies_interests.length > 0 ? (
                  <div className="flex flex-wrap gap-2">
                    {candidate.hobbies_interests.map((hobby, index) => (
                      <Badge key={index} variant="outline">
                        {hobby}
                      </Badge>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No hobbies listed
                  </p>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="parsed-data" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Database className="h-4 w-4" />
                  Parsed Resume Data
                </CardTitle>
                <CardDescription>
                  Structured data extracted from the candidate's resume using AI
                </CardDescription>
              </CardHeader>
              <CardContent>
                {renderParsedResumeData(candidate.parsed_resume_data)}
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="raw-resume" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Raw Resume Markdown
                </CardTitle>
                <CardDescription>
                  Original text content extracted from the PDF resume
                </CardDescription>
              </CardHeader>
              <CardContent>
                {candidate.resume_markdown ? (
                  <div className="prose-sm dark:prose-invert max-w-none bg-gray-50 p-4 rounded-lg border max-h-96 overflow-y-auto">
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                      {candidate.resume_markdown}
                    </ReactMarkdown>
                  </div>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No resume markdown available
                  </p>
                )}
              </CardContent>
            </Card>

            {candidate.resume_url && (
              <Card>
                <CardHeader>
                  <CardTitle>Resume File</CardTitle>
                </CardHeader>
                <CardContent>
                  <Button
                    onClick={() => window.open(candidate.resume_url!, "_blank")}
                    variant="outline"
                  >
                    View Original Resume PDF
                  </Button>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
